import { FileCheck, Shield, ListChecks } from 'lucide-react';

export default function VerificationSection() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-ink-900 to-ink-950 py-24">
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid items-center gap-16 lg:grid-cols-2">
          <div>
            <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-rose-400">
              Built for the work, not the demo
            </p>
            <h2 className="mb-6 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Every answer comes with a <span className="text-rose-400">receipt</span>.
            </h2>
            <p className="mb-8 text-lg leading-relaxed text-slate-400">
              Most AI tools hand you a confident-sounding answer and hope
              you don't ask where it came from. The One Research is built
              the other way around: every fact carries a source URL, a
              confidence score, and the chain of work that produced it —
              ready for an auditor, a compliance review, or just an honest
              CRM note.
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-rose-500/10 ring-1 ring-rose-500/20">
                  <FileCheck className="h-5 w-5 text-rose-400" strokeWidth={1.75} />
                </div>
                <div>
                  <p className="font-semibold text-white">Source-cited findings</p>
                  <p className="text-sm text-slate-400">
                    Every claim links back to the page, call, or document it came
                    from. Open the receipt to see the verbatim excerpt.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-violet-500/10 ring-1 ring-violet-500/20">
                  <ListChecks className="h-5 w-5 text-violet-400" strokeWidth={1.75} />
                </div>
                <div>
                  <p className="font-semibold text-white">Confidence + review queue</p>
                  <p className="text-sm text-slate-400">
                    High-confidence rates auto-apply. Anything ambiguous lands
                    in a review queue with the conflicting sources called out
                    side by side.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-cyan-500/10 ring-1 ring-cyan-500/20">
                  <Shield className="h-5 w-5 text-cyan-400" strokeWidth={1.75} />
                </div>
                <div>
                  <p className="font-semibold text-white">Audit-grade trail</p>
                  <p className="text-sm text-slate-400">
                    Every step — web search, voice call, email send — is
                    timestamped, costed, and exportable. SOC 2 / compliance
                    teams get what they need without a Jira chase.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-rose-500/10 via-violet-500/5 to-cyan-500/10 blur-3xl" />
            <div className="rounded-2xl border border-white/10 bg-ink-900/80 p-8 backdrop-blur">
              <p className="mb-2 text-xs uppercase tracking-wider text-slate-500">
                Sample finding
              </p>
              <p className="mb-1 text-xl font-bold text-white">Acme Industries · Director of IT</p>
              <p className="mb-6 text-sm text-slate-400">Jane Doe · jane.doe@acme.com</p>

              <div className="space-y-3">
                <Receipt
                  label="Title verified"
                  value="LinkedIn /in/jane-doe-acme — confirmed"
                  confidence={0.92}
                />
                <Receipt
                  label="Email pattern"
                  value="acme.com follows first.last@ — verified by SES"
                  confidence={0.95}
                />
                <Receipt
                  label="Recent trigger"
                  value="Promoted from IT Manager — Aug 2026"
                  confidence={0.81}
                />
                <Receipt
                  label="Best contact channel"
                  value="Office line — answers between 9–11am"
                  confidence={0.74}
                />
              </div>

              <div className="mt-6 rounded-xl border border-rose-500/20 bg-rose-500/5 p-4">
                <p className="text-sm font-semibold text-rose-300">
                  Click any line for the full source.
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  Citations, transcripts, screenshots — exportable as PDF or JSON.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Receipt({
  label,
  value,
  confidence,
}: {
  label: string;
  value: string;
  confidence: number;
}) {
  const pct = Math.round(confidence * 100);
  const color =
    confidence >= 0.85
      ? 'text-emerald-400'
      : confidence >= 0.7
        ? 'text-amber-400'
        : 'text-rose-400';
  return (
    <div className="rounded-xl border border-white/5 bg-white/5 p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
        <span className={`font-mono text-xs font-semibold ${color}`}>{pct}%</span>
      </div>
      <p className="mt-1 text-sm text-slate-200">{value}</p>
    </div>
  );
}
